// ═══════════════════════════════════════════════════════
// useSocket — manages Socket.IO connection lifetime
// Reconnects when token changes. Exposes event emitter.
// ═══════════════════════════════════════════════════════
import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { getToken } from '../api/client';

type EventHandler = (event: { type: string; payload: unknown; timestamp: string }) => void;

let _socket: Socket | null = null;
const _handlers = new Map<string, Set<EventHandler>>();

function getSocket(token: string): Socket {
  if (_socket && _socket.connected) return _socket;
  if (_socket) _socket.disconnect();

  _socket = io('/', {
    auth:       { token },
    transports: ['websocket', 'polling'],
    reconnectionAttempts: 10,
    reconnectionDelay:    2000,
  });

  // Fan out to all registered handlers
  const WS_EVENTS = [
    'alert:new', 'alert:updated', 'agent:status',
    'product:score_updated', 'negotiation:update', 'system:heartbeat',
  ];
  for (const evt of WS_EVENTS) {
    _socket.on(evt, (data: unknown) => {
      const handlers = _handlers.get(evt);
      if (handlers) handlers.forEach(h => h(data as Parameters<EventHandler>[0]));
    });
  }

  return _socket;
}

export function useSocket(handlers: Record<string, EventHandler>) {
  const token = getToken();
  const handlersRef = useRef(handlers);

  // Keep handlersRef current after every render (must be in effect, not render body)
  useEffect(() => { handlersRef.current = handlers; });

  // Register handlers in the global map
  useEffect(() => {
    const entries = Object.entries(handlersRef.current) as [string, EventHandler][];
    for (const [evt, fn] of entries) {
      if (!_handlers.has(evt)) _handlers.set(evt, new Set());
      _handlers.get(evt)!.add(fn);
    }
    return () => {
      for (const [evt, fn] of entries) {
        _handlers.get(evt)?.delete(fn);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Connect / reconnect when token changes
  useEffect(() => {
    if (!token) return;
    getSocket(token);
    return () => {
      // Don't disconnect on unmount — keep the shared socket alive
    };
  }, [token]);

  const subscribe = useCallback((room: string) => {
    _socket?.emit(`subscribe:${room}`);
  }, []);

  const unsubscribe = useCallback((room: string) => {
    _socket?.emit(`unsubscribe:${room}`);
  }, []);

  return { subscribe, unsubscribe };
}

// Call once at app root to initialise the socket
export function initSocket(token: string) {
  getSocket(token);
  _socket?.emit('subscribe:alerts');
}

export function disconnectSocket() {
  _socket?.disconnect();
  _socket = null;
}
